import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";

export interface StandupTask {
  id: string;
  task_id: string;
  task_type: 'foundational' | 'growth' | 'recurring';
  planned_for_date: string;
  original_due_date: string | null;
  brand_id: string;
  user_id: string;
  status_at_end: string | null;
  created_at: string;
  updated_at: string;
  // Joined task data
  task_title?: string;
  task_status?: string;
  time_spent?: number;
  brand_name?: string;
  brand_slug?: string;
}

export interface TaskForStandup {
  id: string;
  title: string;
  type: 'foundational' | 'growth' | 'recurring';
  status: string;
  due_date: string | null;
  time_estimated?: number;
  time_spent?: number;
  brand_id: string;
  brand_name: string;
  brand_slug: string;
}

export const useGrowthStandup = () => {
  const [standupTasks, setStandupTasks] = useState<StandupTask[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingStandup, setIsLoadingStandup] = useState(true); // Initial load state
  const { user } = useAuth();
  const { toast } = useToast();

  const getTodayDateString = () => {
    return format(new Date(), 'yyyy-MM-dd');
  };

  // Fetch today's standup tasks - time_spent calculated from audit log
  const fetchTodayStandup = useCallback(async () => {
    if (!user) return;

    setIsLoadingStandup(true);
    try {
      const today = getTodayDateString();
      
      const { data, error } = await (supabase
        .from('os_growth_standup' as any)
        .select('*')
        .eq('user_id', user.id)
        .eq('planned_for_date', today)
        .order('created_at', { ascending: true }) as unknown as Promise<{ data: any[]; error: any }>);

      if (error) throw error;

      if (!data || data.length === 0) {
        setStandupTasks([]);
        return;
      }

      // Group task IDs by type for batch fetching
      const foundationalIds = data.filter(s => s.task_type === 'foundational').map(s => s.task_id);
      const growthIds = data.filter(s => s.task_type === 'growth').map(s => s.task_id);
      const recurringIds = data.filter(s => s.task_type === 'recurring').map(s => s.task_id);
      const allTaskIds = [...foundationalIds, ...growthIds, ...recurringIds];

      console.log('[useGrowthStandup] Fetching for task IDs:', allTaskIds.length, allTaskIds);

      // Fetch all tasks in parallel by type (for titles/status)
      const [foundationalRes, growthRes, recurringRes] = await Promise.all([
        foundationalIds.length > 0
          ? supabase
              .from('foundational_tasks')
              .select('id, title, status, brands(name, slug)')
              .in('id', foundationalIds)
          : Promise.resolve({ data: [] }),
        growthIds.length > 0
          ? supabase
              .from('growth_tasks')
              .select('id, name, task_status, brands(name, slug)')
              .in('id', growthIds)
          : Promise.resolve({ data: [] }),
        recurringIds.length > 0
          ? supabase
              .from('recurring_tasks')
              .select('id, title, status, brands(name, slug)')
              .in('id', recurringIds)
          : Promise.resolve({ data: [] }),
      ]);

      // Fetch time_spent from audit log SEPARATELY - this is the source of truth
      // IMPORTANT: Only fetch TODAY's time entries, not historical data
      let timeTrackingRes: { data: any[] | null } = { data: [] };
      if (allTaskIds.length > 0) {
        const todayStart = `${today}T00:00:00`;
        const tomorrowStart = format(new Date(new Date().setDate(new Date().getDate() + 1)), 'yyyy-MM-dd') + 'T00:00:00';
        
        const { data: timeData, error: timeError } = await supabase
          .from('os_universal_time_tracking')
          .select('task_id, minutes_added')
          .in('task_id', allTaskIds)
          .eq('entry_type', 'time_spent')
          .gte('created_at', todayStart)
          .lt('created_at', tomorrowStart);
        
        if (timeError) {
          console.error('[useGrowthStandup] Error fetching time tracking:', timeError);
        } else {
          console.log('[useGrowthStandup] Raw time tracking data for today:', timeData?.length, 'entries');
          timeTrackingRes = { data: timeData };
        }
      }

      // Create lookup maps for O(1) access
      const foundationalMap = new Map((foundationalRes.data || []).map((t: any) => [t.id, t]));
      const growthMap = new Map((growthRes.data || []).map((t: any) => [t.id, t]));
      const recurringMap = new Map((recurringRes.data || []).map((t: any) => [t.id, t]));

      // Calculate time_spent per task by summing audit log entries
      const timeSpentMap = new Map<string, number>();
      (timeTrackingRes.data || []).forEach((entry: any) => {
        const current = timeSpentMap.get(entry.task_id) || 0;
        timeSpentMap.set(entry.task_id, current + (entry.minutes_added || 0));
      });

      console.log('[useGrowthStandup] Time from audit log - tasks with time:', timeSpentMap.size, 'of', allTaskIds.length, 'total tasks');
      console.log('[useGrowthStandup] Time map:', Object.fromEntries(timeSpentMap));

      // Enrich standup tasks with task details
      const enrichedTasks: StandupTask[] = data.map(standup => {
        let taskDetails: any = null;
        
        if (standup.task_type === 'foundational') {
          taskDetails = foundationalMap.get(standup.task_id);
        } else if (standup.task_type === 'growth') {
          const task = growthMap.get(standup.task_id);
          taskDetails = task ? { ...task, title: task.name, status: task.task_status } : null;
        } else if (standup.task_type === 'recurring') {
          taskDetails = recurringMap.get(standup.task_id);
        }

        // Get time_spent from audit log sum, not from task table
        const timeSpent = Math.max(0, timeSpentMap.get(standup.task_id) || 0);

        return {
          ...standup,
          task_title: taskDetails?.title || 'Unknown Task',
          task_status: taskDetails?.status || 'pending',
          time_spent: timeSpent,
          brand_name: taskDetails?.brands?.name || 'Unknown Brand',
          brand_slug: taskDetails?.brands?.slug || '',
        };
      });

      setStandupTasks(enrichedTasks);
    } catch (error) {
      console.error('Error fetching standup:', error);
      toast({
        title: "Error",
        description: "Failed to fetch today's standup",
        variant: "destructive",
      });
    } finally {
      setIsLoadingStandup(false);
    }
  }, [user, toast]);

  // Add tasks to today's standup
  const addToStandup = async (tasks: TaskForStandup[]) => {
    if (!user) return false;

    setIsLoading(true);
    try {
      const today = getTodayDateString();
      
      // Check for existing tasks in today's standup to avoid duplicates
      const { data: existing } = await (supabase
        .from('os_growth_standup' as any)
        .select('task_id')
        .eq('user_id', user.id)
        .eq('planned_for_date', today) as unknown as Promise<{ data: any[]; error: any }>);

      const existingTaskIds = new Set((existing || []).map((e: any) => e.task_id));
      const newTasks = tasks.filter(t => !existingTaskIds.has(t.id));

      if (newTasks.length === 0) {
        toast({
          title: "No new tasks",
          description: "All selected tasks are already in today's standup",
        });
        setIsLoading(false);
        return false;
      }

      // Insert standup records
      const standupRecords = newTasks.map(task => ({
        task_id: task.id,
        task_type: task.type,
        planned_for_date: today,
        original_due_date: task.due_date,
        brand_id: task.brand_id,
        user_id: user.id,
      }));

      const { error: insertError } = await (supabase
        .from('os_growth_standup' as any)
        .insert(standupRecords) as unknown as Promise<{ data: any; error: any }>);

      if (insertError) throw insertError;

      // Update task statuses to in_progress if they are pending
      for (const task of newTasks) {
        if (task.status === 'pending') {
          if (task.type === 'foundational') {
            await supabase
              .from('foundational_tasks')
              .update({ status: 'in_progress' })
              .eq('id', task.id);
          } else if (task.type === 'growth') {
            await supabase
              .from('growth_tasks')
              .update({ task_status: 'in_progress' })
              .eq('id', task.id);
          } else if (task.type === 'recurring') {
            await supabase
              .from('recurring_tasks')
              .update({ status: 'in_progress' })
              .eq('id', task.id);
          }
        }
      }

      toast({
        title: "Tasks added",
        description: `${newTasks.length} task(s) added to today's standup`,
      });

      await fetchTodayStandup();
      return true;
    } catch (error) {
      console.error('Error adding to standup:', error);
      toast({
        title: "Error",
        description: "Failed to add tasks to standup",
        variant: "destructive",
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Remove a task from today's standup
  const removeFromStandup = async (standupId: string) => {
    if (!user) return false;

    try {
      const { error } = await (supabase
        .from('os_growth_standup' as any)
        .delete()
        .eq('id', standupId)
        .eq('user_id', user.id) as unknown as Promise<{ data: any; error: any }>);

      if (error) throw error;

      setStandupTasks(prev => prev.filter(t => t.id !== standupId));

      toast({
        title: "Removed",
        description: "Task removed from standup",
      });
      return true;
    } catch (error) {
      console.error('Error removing from standup:', error);
      toast({
        title: "Error",
        description: "Failed to remove task from standup",
        variant: "destructive",
      });
      return false;
    }
  };

  // Update task status on the original table
  const updateTaskStatus = async (
    taskId: string, 
    taskType: 'foundational' | 'growth' | 'recurring', 
    status: string
  ) => {
    try {
      if (taskType === 'foundational') {
        await supabase
          .from('foundational_tasks')
          .update({ status })
          .eq('id', taskId);
      } else if (taskType === 'growth') {
        await supabase
          .from('growth_tasks')
          .update({ task_status: status })
          .eq('id', taskId);
      } else if (taskType === 'recurring') {
        await supabase
          .from('recurring_tasks')
          .update({ status })
          .eq('id', taskId);
      }

      // Update local state
      setStandupTasks(prev => 
        prev.map(t => t.task_id === taskId ? { ...t, task_status: status } : t)
      );

      return true;
    } catch (error) {
      console.error('Error updating task status:', error);
      toast({
        title: "Error",
        description: "Failed to update task status",
        variant: "destructive",
      });
      return false;
    }
  };

  // Update time spent - audit log is source of truth
  // minutesToAdd is the increment, not the total
  const updateTimeSpent = async (
    taskId: string, 
    taskType: 'foundational' | 'growth' | 'recurring', 
    minutesToAdd: number,
    source: 'quick_add_15' | 'quick_add_30' | 'quick_add_60' | 'manual' | 'adjustment' = 'manual'
  ) => {
    try {
      // Step 1: Get brand_id from task table (just for the FK reference)
      let brandId: string | undefined;

      if (taskType === 'foundational') {
        const { data, error } = await supabase
          .from('foundational_tasks')
          .select('brand_id')
          .eq('id', taskId)
          .single();
        if (error) throw error;
        brandId = data?.brand_id;
      } else if (taskType === 'growth') {
        const { data, error } = await supabase
          .from('growth_tasks')
          .select('brand_id')
          .eq('id', taskId)
          .single();
        if (error) throw error;
        brandId = data?.brand_id;
      } else if (taskType === 'recurring') {
        const { data, error } = await supabase
          .from('recurring_tasks')
          .select('brand_id')
          .eq('id', taskId)
          .single();
        if (error) throw error;
        brandId = data?.brand_id;
      }

      // Step 2: Calculate current total from AUDIT LOG (source of truth)
      // IMPORTANT: Only sum TODAY's entries, not historical data
      const today = format(new Date(), 'yyyy-MM-dd');
      const todayStart = `${today}T00:00:00`;
      const tomorrowStart = format(new Date(new Date().setDate(new Date().getDate() + 1)), 'yyyy-MM-dd') + 'T00:00:00';
      
      const { data: auditData, error: auditError } = await supabase
        .from('os_universal_time_tracking')
        .select('minutes_added')
        .eq('task_id', taskId)
        .eq('entry_type', 'time_spent')
        .gte('created_at', todayStart)
        .lt('created_at', tomorrowStart);
      
      if (auditError) throw auditError;

      const currentAuditTotal = (auditData || []).reduce((sum, entry) => sum + (entry.minutes_added || 0), 0);
      const newTotal = Math.max(0, currentAuditTotal + minutesToAdd);

      console.log('[updateTimeSpent] Audit log total:', currentAuditTotal, '+ adding:', minutesToAdd, '= new:', newTotal);

      // Step 3: Insert audit entry FIRST (this is the source of truth)
      if (minutesToAdd !== 0 && brandId && user) {
        const { error: insertError } = await supabase
          .from('os_universal_time_tracking')
          .insert({
            brand_id: brandId,
            user_id: user.id,
            task_id: taskId,
            task_type: taskType,
            entry_type: 'time_spent',
            minutes_added: minutesToAdd,
            previous_total: currentAuditTotal,
            new_total: newTotal,
            source: source,
          });
        
        if (insertError) throw insertError;
      }

      // Step 4: Update React state with new total from audit
      setStandupTasks(prev => 
        prev.map(t => t.task_id === taskId ? { ...t, time_spent: newTotal } : t)
      );

      // Step 5: Also sync task table (for backwards compatibility, but audit is truth)
      if (taskType === 'foundational') {
        await supabase.from('foundational_tasks').update({ time_spent: newTotal }).eq('id', taskId);
      } else if (taskType === 'growth') {
        await supabase.from('growth_tasks').update({ time_spent: newTotal }).eq('id', taskId);
      } else if (taskType === 'recurring') {
        await supabase.from('recurring_tasks').update({ time_spent: newTotal }).eq('id', taskId);
      }

      return true;
    } catch (error) {
      console.error('Error updating time spent:', error);
      toast({
        title: "Error",
        description: "Failed to save time. Refreshing data...",
        variant: "destructive",
      });
      // Refetch to resync with DB truth
      await fetchTodayStandup();
      return false;
    }
  };

  // Helper to format time
  const formatTimeString = (minutes: number | undefined | null): string => {
    if (!minutes || minutes === 0) return '-';
    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
    }
    return `${minutes}m`;
  };

  // Helper to get type label
  const getTypeLabel = (type: 'foundational' | 'growth' | 'recurring'): string => {
    switch (type) {
      case 'foundational': return 'Foundational';
      case 'growth': return 'Growth';
      case 'recurring': return 'Recurring';
    }
  };

  // Format tasks for Slack message
  const formatForSlack = (tasks: TaskForStandup[], userName: string) => {
    const today = format(new Date(), 'MMMM d, yyyy');
    
    let message = `*Day Start - ${today} - ${userName}*\n\n`;

    tasks.forEach(task => {
      const dueDate = task.due_date ? format(new Date(task.due_date), 'MMM d') : '-';
      const estTime = formatTimeString(task.time_estimated);
      const spentTime = formatTimeString(task.time_spent);
      
      message += `${task.brand_name} - ${task.title} - Due: ${dueDate} - Est: ${estTime} - Spent: ${spentTime}\n`;
    });

    const totalEstimated = tasks.reduce((acc, t) => acc + (t.time_estimated || 0), 0);
    const totalSpent = tasks.reduce((acc, t) => acc + (t.time_spent || 0), 0);
    
    message += `\n*Total: ${tasks.length} tasks | Est: ${formatTimeString(totalEstimated)} | Spent: ${formatTimeString(totalSpent)}*`;

    return message;
  };

  // Copy tasks to clipboard
  const copyToClipboard = async (tasks: TaskForStandup[], userName: string) => {
    const text = formatForSlack(tasks, userName).replace(/\*/g, '');
    try {
      await navigator.clipboard.writeText(text);
      toast({
        title: "Copied",
        description: "Standup copied to clipboard",
      });
      return true;
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to copy to clipboard",
        variant: "destructive",
      });
      return false;
    }
  };

  // Check if there are tasks in today's standup
  const hasTodayStandup = standupTasks.length > 0;

  useEffect(() => {
    fetchTodayStandup();
  }, [fetchTodayStandup]);

  return {
    standupTasks,
    isLoading,
    isLoadingStandup,
    hasTodayStandup,
    addToStandup,
    removeFromStandup,
    updateTaskStatus,
    updateTimeSpent,
    formatForSlack,
    copyToClipboard,
    refetch: fetchTodayStandup,
  };
};
